import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Row, Col, Spinner, Button, Card, CardBody } from 'reactstrap';
import {
  BarChart2, TrendingUp, TrendingDown, Package, Calendar, Filter,
  ChevronLeft, ChevronRight, Download
} from 'react-feather';
import http from '../../../components/network';
import downloadExcel from '../../../utils/downloadExcel';

const StockCard = () => {
  const { t } = useTranslation();

  const [exporting, setExporting] = useState(false);

  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);

  const fetchItems = async () => {
    setLoadingItems(true);
    try {
      const response = await http.get('/inventory/master-data');
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchStockCard = async () => {
    if (!selectedItem) return;
    setLoading(true);
    setPage(1); // reset pagination
    try {
      const response = await http.get('/inventory/stock-card', {
        params: {
          item_id: selectedItem,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        },
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching stock card:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('id-ID').format(parseFloat(num) || 0);
  };

  const selectedItemName = items.find(
    (i) => String(i.id) === String(selectedItem)
  )?.item_name;

  // Ambil nama base unit dari data response atau list items
  const baseUnitName =
    data?.item?.unit_item?.unit_name ||
    items.find((i) => String(i.id) === String(selectedItem))?.unit_item?.unit_name ||
    '';

  const handleExport = async () => {
    if (!selectedItem) return;
    setExporting(true);
    try {
      await downloadExcel(
        '/inventory/export/stock-card',
        {
          item_id: selectedItem,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        },
        `stock-card-${selectedItemName || 'item'}.xlsx`
      );
    } catch (err) {
      alert('Gagal export Excel');
    } finally {
      setExporting(false);
    }
  };

  const getDocTypeStyle = (type) => {
    if (type === 'Purchase Invoice') return { backgroundColor: '#d1fae5', color: '#065f46' };
    if (
      type === 'Pharmacy Prescription' ||
      type === 'Pharmacy Compound' ||
      type === 'Pharmacy Sale'
    ) {
      return { backgroundColor: '#fee2e2', color: '#991b1b' };
    }
    if (type === 'Refund' || type === 'Purchase Return') {
      return { backgroundColor: '#fef3c7', color: '#92400e' };
    }
    return { backgroundColor: '#e5e7eb', color: '#1f2937' };
  };

  const badgeStyle = (type) => ({
    padding: '4px 10px',
    borderRadius: '10px',
    fontSize: '10.5px',
    fontWeight: 600,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    ...getDocTypeStyle(type),
  });

  // Pagination calculations
  const mutations = data?.mutations || [];
  const totalPages = Math.ceil(mutations.length / perPage);
  const startIdx = (page - 1) * perPage;
  const endIdx = startIdx + perPage;
  const paginatedMutations = mutations.slice(startIdx, endIdx);

  return (
    <div>
      {/* ═══════ FILTER CARD ═══════ */}
      <Card className="border-0 shadow-sm mb-3">
        <CardBody className="p-3">
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded"
              style={{ width: '32px', height: '32px', backgroundColor: '#eff6ff', color: '#3b82f6' }}
            >
              <Filter size={16} />
            </div>
            <div>
              <h6 className="fw-bold mb-0" style={{ color: '#1e293b' }}>
                {t('Filter')}
              </h6>
              <small className="text-muted">{t('Select item and period')}</small>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '4fr 3fr 3fr 2fr',
              gap: '12px',
              alignItems: 'end',
            }}
          >
            {/* Item */}
            <div>
              <div
                className="small fw-bold text-muted text-uppercase"
                style={{ fontSize: '10px', letterSpacing: '0.5px', marginBottom: '6px' }}
              >
                <Package size={12} className="me-1" />
                {t('Select Item')}
              </div>
              <Input
                type="select"
                value={selectedItem}
                onChange={(e) => setSelectedItem(e.target.value)}
                disabled={loadingItems}
                style={{ borderRadius: '8px', fontSize: '13px', height: '38px' }}
              >
                <option value="">{t('-- Select Item --')}</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.item_name}
                  </option>
                ))}
              </Input>
            </div>

            {/* Start Date */}
            <div>
              <div
                className="small fw-bold text-muted text-uppercase"
                style={{ fontSize: '10px', letterSpacing: '0.5px', marginBottom: '6px' }}
              >
                <Calendar size={12} className="me-1" />
                {t('Start Date')}
              </div>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ borderRadius: '8px', fontSize: '13px', height: '38px' }}
              />
            </div>

            {/* End Date */}
            <div>
              <div
                className="small fw-bold text-muted text-uppercase"
                style={{ fontSize: '10px', letterSpacing: '0.5px', marginBottom: '6px' }}
              >
                <Calendar size={12} className="me-1" />
                {t('End Date')}
              </div>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ borderRadius: '8px', fontSize: '13px', height: '38px' }}
              />
            </div>

            {/* Button */}
            <div>
              <Button
                color="primary"
                onClick={fetchStockCard}
                disabled={!selectedItem || loading}
                block
                style={{ borderRadius: '8px', fontWeight: 600, height: '38px', width: '100%' }}
              >
                {loading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <BarChart2 size={14} className="me-1" />
                    {t('Show')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ═══════ RESULT ═══════ */}
      {data && (
        <>
          {selectedItemName && (
            <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <h5 className="fw-bold mb-0" style={{ color: '#1e293b' }}>
                  {selectedItemName}
                </h5>
                <small className="text-muted">
                  {t('Stock card for period')} {startDate || t('beginning')} — {endDate || t('now')}
                </small>
              </div>
            </div>
          )}

          {/* ═══════ SUMMARY CARDS ═══════ */}
          <Row className="g-3 mb-3">
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #6b7280' }}>
                <CardBody className="p-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Package size={14} className="text-muted" />
                    <small
                      className="text-muted text-uppercase fw-bold"
                      style={{ fontSize: '10px', letterSpacing: '0.5px' }}
                    >
                      {t('Opening Balance')}
                    </small>
                  </div>
                  {/* 1. Saldo Awal */}
                  <h4 className="fw-bold mb-0" style={{ color: '#374151' }}>
                    {formatNumber(data.opening_balance)}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #10b981' }}>
                <CardBody className="p-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <TrendingUp size={14} className="text-success" />
                    <small
                      className="text-muted text-uppercase fw-bold"
                      style={{ fontSize: '10px', letterSpacing: '0.5px' }}
                    >
                      {t('Total In')}
                    </small>
                  </div>
                  {/* 2. Total Masuk */}
                  <h4 className="fw-bold mb-0 text-success">
                    +{formatNumber(data.total_in)}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #ef4444' }}>
                <CardBody className="p-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <TrendingDown size={14} className="text-danger" />
                    <small
                      className="text-muted text-uppercase fw-bold"
                      style={{ fontSize: '10px', letterSpacing: '0.5px' }}
                    >
                      {t('Total Out')}
                    </small>
                  </div>
                  {/* 3. Total Keluar */}
                  <h4 className="fw-bold mb-0 text-danger">
                    -{formatNumber(data.total_out)}
                  </h4>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #3b82f6' }}>
                <CardBody className="p-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Package size={14} className="text-primary" />
                    <small
                      className="text-muted text-uppercase fw-bold"
                      style={{ fontSize: '10px', letterSpacing: '0.5px' }}
                    >
                      {t('Closing Balance')}
                    </small>
                  </div>
                  {/* 4. Saldo Akhir */}
                  <h4 className="fw-bold mb-0 text-primary">
                    {formatNumber(data.closing_balance)}
                  </h4>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ═══════ TABLE MUTASI (CSS GRID) ═══════ */}
          <Card className="border-0 shadow-sm">
            <CardBody className="p-0">
              <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="fw-bold mb-0" style={{ color: '#1e293b' }}>
                    {t('Stock Movements')}
                  </h6>
                  <small className="text-muted">
                    {data.mutations.length} {t('transactions')}
                  </small>
                </div>
                <Button
                  color="success"
                  size="sm"
                  onClick={handleExport}
                  disabled={exporting}
                  className="d-flex align-items-center gap-1"
                  style={{ borderRadius: '8px', fontWeight: 600 }}
                >
                  {exporting ? <Spinner size="sm" /> : <><Download size={14} /> {t('Export Excel')}</>}
                </Button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                {/* Header */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '100px 140px 160px 120px 110px 3fr 90px 90px 100px',
                    gap: 0,
                    background: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: '#6b7280',
                    fontWeight: 700,
                    minWidth: '1200px',
                  }}
                >
                  <div style={{ padding: '10px 12px' }}>{t('Date')}</div>
                  <div style={{ padding: '10px 12px' }}>{t('Doc No')}</div>
                  <div style={{ padding: '10px 12px' }}>{t('Type')}</div>
                  <div style={{ padding: '10px 12px' }}>{t('Batch')}</div>
                  <div style={{ padding: '10px 12px' }}>{t('Expired')}</div>
                  <div style={{ padding: '10px 12px' }}>{t('Supplier')}</div>
                  <div style={{ padding: '10px 12px', textAlign: 'right' }}>{t('In')} {baseUnitName ? `(${baseUnitName})` : ''}</div>
                  <div style={{ padding: '10px 12px', textAlign: 'right' }}>{t('Out')} {baseUnitName ? `(${baseUnitName})` : ''}</div>
                  <div style={{ padding: '10px 12px', textAlign: 'right' }}>{t('Remaining')} {baseUnitName ? `(${baseUnitName})` : ''}</div>
                </div>

                {/* Body */}
                {paginatedMutations.length === 0 ? (
                  <div className="text-center text-muted py-5" style={{ minWidth: '1200px' }}>
                    <Package size={32} className="mb-2" style={{ opacity: 0.3 }} />
                    <div>{t('No movements in this period')}</div>
                  </div>
                ) : (
                  paginatedMutations.map((row, idx) => (
                    <div
                      key={row.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '100px 140px 160px 120px 110px 3fr 90px 90px 100px',
                        gap: 0,
                        borderBottom: '1px solid #f1f5f9',
                        fontSize: '13px',
                        background: idx % 2 === 0 ? '#fff' : '#fafafa',
                        minWidth: '1200px',
                      }}
                    >
                      <div
                        style={{
                          padding: '10px 12px',
                          color: '#6b7280',
                          whiteSpace: 'nowrap',
                          fontSize: '12px',
                        }}
                      >
                        {new Date(row.document_date).toLocaleDateString('id-ID', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </div>
                      <div
                        style={{
                          padding: '10px 12px',
                          fontWeight: 600,
                          fontSize: '12px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={row.document_number}
                      >
                        {row.document_number}
                      </div>
                      <div style={{ padding: '10px 12px' }}>
                        <span style={badgeStyle(row.document_type)}>{row.document_type}</span>
                      </div>
                      <div
                        style={{
                          padding: '10px 12px',
                          color: '#6b7280',
                          fontSize: '12px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={row.batch_number || '-'}
                      >
                        {row.batch_number || '-'}
                      </div>
                      <div
                        style={{
                          padding: '10px 12px',
                          color: '#6b7280',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.expired
                          ? new Date(row.expired).toLocaleDateString('id-ID', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })
                          : '-'}
                      </div>
                      <div
                        style={{
                          padding: '10px 12px',
                          color: '#6b7280',
                          fontSize: '12px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={row.supplier?.supplier_name || '-'}
                      >
                        {row.supplier?.supplier_name || '-'}
                      </div>
                      <div
                        style={{
                          padding: '10px 12px',
                          textAlign: 'right',
                          fontWeight: 700,
                          color: '#065f46',
                        }}
                      >
                        {row.actual_qty > 0 ? `+${formatNumber(row.actual_qty)}` : '-'}
                      </div>
                      <div
                        style={{
                          padding: '10px 12px',
                          textAlign: 'right',
                          fontWeight: 700,
                          color: '#991b1b',
                        }}
                      >
                        {row.actual_qty < 0
                          ? `-${formatNumber(Math.abs(row.actual_qty))}`
                          : '-'}
                      </div>
                      <div
                        style={{
                          padding: '10px 12px',
                          textAlign: 'right',
                          fontWeight: 700,
                          color: '#1e293b',
                        }}
                      >
                        {formatNumber(row.running_balance)}
                      </div>
                    </div>
                  ))
                )}

                {/* ═══════ PAGINATION ═══════ */}
                {mutations.length > 0 && (
                  <div
                    className="d-flex justify-content-between align-items-center p-3"
                    style={{
                      borderTop: '1px solid #e2e8f0',
                      minWidth: '1200px',
                      background: '#fff',
                    }}
                  >
                    <small className="text-muted">
                      {t('Showing')} <strong>{startIdx + 1}</strong>–<strong>{Math.min(endIdx, mutations.length)}</strong> {t('of')} <strong>{mutations.length}</strong> {t('items')}
                    </small>

                    <div className="d-flex align-items-center gap-2">
                      <Button
                        size="sm"
                        color="light"
                        className="border d-flex align-items-center"
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                        style={{ borderRadius: '6px' }}
                      >
                        <ChevronLeft size={14} />
                      </Button>

                      <span className="text-muted small px-2">
                        {t('Page')} <strong>{page}</strong> {t('of')} <strong>{totalPages || 1}</strong>
                      </span>

                      <Button
                        size="sm"
                        color="light"
                        className="border d-flex align-items-center"
                        disabled={page === totalPages || totalPages === 0}
                        onClick={() => setPage(page + 1)}
                        style={{ borderRadius: '6px' }}
                      >
                        <ChevronRight size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </>
      )}

      {/* ═══════ EMPTY STATE ═══════ */}
      {!data && !loading && (
        <div className="text-center py-5 text-muted">
          <Package size={48} style={{ opacity: 0.2 }} className="mb-3" />
          <h6 className="mb-1">{t('No data yet')}</h6>
          <small>{t('Select an item and period to view the stock card')}</small>
        </div>
      )}
    </div>
  );
};

export default StockCard;